import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLookup } from '../contexts/LookupContext';
import {
  AddressBook, Users, CalendarBlank, ListChecks,
  ChartBar, UploadSimple, GearSix, SignOut, MagnifyingGlass, Lightning, FunnelSimple,
  List, FileText, Notebook, Printer, Question, ShareNetwork, Sparkle, X, Gauge, GitMerge, FilmStrip
} from '@phosphor-icons/react';
import api from '../lib/api';
import QuickAdd from './QuickAdd';
import AlarmsModal from './AlarmsModal';
import WhatsNewToast from './WhatsNewToast';
import AIAssistant from './AIAssistant';
import ActivityReminderPoller from './ActivityReminderPoller';
import ProductTour from './ProductTour';

const navItems = [
  { to: '/contact', label: 'Contacts', icon: AddressBook },
  { to: '/contacts', label: 'Contact List', icon: List },
  { to: '/search', label: 'Search', icon: MagnifyingGlass },
  { to: '/groups', label: 'Groups', icon: Users },
  { to: '/calendar', label: 'Calendar', icon: CalendarBlank },
  { to: '/tasks', label: 'Task List', icon: ListChecks },
  { to: '/pipeline', label: 'Opportunities', icon: FunnelSimple },
  { to: '/reports', label: 'Reports', icon: FileText },
  { to: '/templates', label: 'Templates', icon: Notebook },
  { to: '/print', label: 'Print & Merge', icon: Printer },
  { to: '/dashboard', label: 'Dashboard', icon: ChartBar },
  { to: '/smart-import', label: 'Smart Import', icon: Sparkle, highlight: true },
  { to: '/sheet-sync', label: 'Sheet Sync', icon: ShareNetwork, premium: true },
  { to: '/import-export', label: 'Import / Export', icon: UploadSimple },
  { to: '/kit', label: 'Marketing Kit', icon: ShareNetwork, adminOnly: true },
  { to: '/help', label: 'Help', icon: Question },
  { to: '/settings', label: 'Settings', icon: GearSix },
  { to: '/admin/audit', label: 'System Audit', icon: Gauge, adminOnly: true },
  { to: '/admin/merge', label: 'Merge Duplicates', icon: GitMerge, adminOrIsolated: true },
  { to: '/admin/videocrunch-jobs', label: 'VideoCrunch Jobs', icon: FilmStrip, adminOrManager: true, vcxBadge: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [vcxPending, setVcxPending] = useState(0);

  const isAdminOrManager = ['admin', 'manager'].includes(user?.role);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setQuickAddOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-close mobile nav on route change
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Red badge: pending VideoCrunch jobs waiting for the production queue.
  // Refreshes on every route change + every 60s so the admin never misses
  // a paying customer's request.
  useEffect(() => {
    if (!isAdminOrManager) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get('/vcx/admin/jobs/pending-count');
        if (!cancelled) setVcxPending(data.count || 0);
      } catch {}
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAdminOrManager, location.pathname]);

  const handleLogout = async () => {
    await logout();
    // Hard navigation — bulletproof against React state-update races and stale modal overlays.
    // Also forces a fresh page load so any in-memory state is fully discarded.
    window.location.href = '/login';
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#E8E8E8]" data-testid="app-layout">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 h-11 bg-gradient-to-b from-[#0B5394] to-[#073763] flex items-center px-3 shadow-md" data-testid="mobile-topbar">
        <button
          onClick={() => setMobileNavOpen(true)}
          className="text-white p-1.5 -ml-1"
          aria-label="Open menu"
          data-testid="mobile-menu-btn"
        >
          <List size={22} weight="bold" />
        </button>
        <span className="ml-2 text-white font-black text-base tracking-tight">
          sales<span className="text-[#F39C12]">Crunch</span>
        </span>
        <div className="flex-1" />
        <button
          onClick={() => setQuickAddOpen(true)}
          className="text-[#F39C12] p-1.5"
          aria-label="Quick add"
          data-testid="mobile-quick-add"
        >
          <Lightning size={18} weight="fill" />
        </button>
      </div>

      {/* Mobile overlay when nav is open */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-overlay"
        />
      )}

      {/* Sidebar (desktop: always visible; mobile: slide-in) */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-56 md:w-48 bg-gradient-to-b from-[#0B5394] to-[#073763] flex flex-col shrink-0 transform transition-transform md:transform-none ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="px-3 pt-3 pb-2 flex items-start">
          <div className="flex-1">
            <span className="text-xl font-black text-white tracking-tight leading-none block" data-testid="app-logo">
              sales<span className="text-[#F39C12]">Crunch</span>
            </span>
            <span className="text-[9px] text-white/60 tracking-[0.15em] uppercase block mt-0.5">CRM</span>
          </div>
          <button
            onClick={() => setMobileNavOpen(false)}
            className="md:hidden text-white/70 hover:text-white p-1"
            aria-label="Close menu"
            data-testid="mobile-nav-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-1 space-y-0.5 overflow-y-auto">
          {navItems.filter(item => {
            if (item.adminOnly) return user?.role === 'admin';
            if (item.adminOrIsolated) return user?.role === 'admin' || user?.is_isolated;
            if (item.adminOrManager) return isAdminOrManager;
            return true;
          }).map(({ to, label, icon: Icon, premium, vcxBadge }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-sm transition-colors ${
                  isActive
                    ? 'bg-white/15 text-white font-semibold'
                    : 'text-[#93C5FD] hover:bg-white/10 hover:text-white'
                }`
              }
              data-testid={`nav-${label.toLowerCase().replace(/[\s\/]+/g, '-')}`}
            >
              <Icon size={15} weight="bold" />
              <span className="flex-1">{label}</span>
              {vcxBadge && vcxPending > 0 && (
                <span
                  className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
                  data-testid="vcx-pending-badge"
                  title={`${vcxPending} video request${vcxPending === 1 ? '' : 's'} waiting`}
                >
                  {vcxPending}
                </span>
              )}
              {premium && (
                <span className="text-[8px] px-1 py-0.5 bg-[#F39C12] text-white font-bold rounded-sm">PRO</span>
              )}
            </NavLink>
          ))}

          <div className="border-t border-white/20 my-2" />

          <button
            onClick={() => setQuickAddOpen(true)}
            className="flex items-center gap-2 px-2.5 py-1.5 text-xs text-[#F39C12] hover:bg-white/10 rounded-sm transition-colors w-full text-left"
            data-testid="sidebar-quick-add"
          >
            <Lightning size={15} weight="bold" />
            Quick Add (Ctrl+K)
          </button>
        </nav>

        {/* User info at bottom */}
        <div className="p-2 border-t border-white/20">
          <NavLink
            to="/settings"
            className="flex items-center gap-2 px-2 py-1.5 mb-1 rounded-sm hover:bg-white/10 transition-colors group"
            data-testid="user-settings-link"
            title="Open Settings"
          >
            <div className="w-6 h-6 rounded-full bg-[#F39C12] flex items-center justify-center text-white text-[10px] font-bold shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white truncate">{user?.name || 'User'}</p>
              <p className="text-[10px] text-white/60 group-hover:text-white/90 truncate">Settings</p>
            </div>
            <GearSix size={12} weight="bold" className="text-white/40 group-hover:text-white shrink-0" />
          </NavLink>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-2.5 py-1 text-[10px] text-[#93C5FD] hover:text-white hover:bg-white/10 rounded-sm transition-colors w-full"
            data-testid="logout-btn"
          >
            <SignOut size={12} weight="bold" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 pt-11 md:pt-0">
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
        <StatusBar user={user} />
      </div>

      <QuickAdd open={quickAddOpen} onOpenChange={setQuickAddOpen} />
      <AlarmsModal user={user} />
      <WhatsNewToast />
      <AIAssistant />
      <ActivityReminderPoller />
      <ProductTour />
    </div>
  );
}

function StatusBar({ user }) {
  const location = useLocation();
  const { activeLookup } = useLookup();
  const [counts, setCounts] = useState(null);
  const [appVersion, setAppVersion] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const api = (await import('../lib/api')).default;
        const [statsR, verR] = await Promise.all([
          api.get('/dashboard/stats').catch(() => null),
          api.get('/version').catch(() => null),
        ]);
        if (cancelled) return;
        if (statsR?.data) setCounts(statsR.data);
        if (verR?.data?.version) setAppVersion(verR.data.version);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  const lookupLabel = (() => {
    // A cross-page active lookup (set on the Contacts card) wins over
    // path-based defaults — ACT!'s persistent-filter behavior.
    if (activeLookup?.label) {
      return typeof activeLookup.count === 'number'
        ? `${activeLookup.label} (${activeLookup.count.toLocaleString()})`
        : activeLookup.label;
    }
    // If a page navigated here with an explicit lookup label (e.g. Alarms,
    // Tasks → contact lookup), prefer it over the path-based default.
    if (location.state?.lookupLabel) return location.state.lookupLabel;
    const p = location.pathname;
    if (p.startsWith('/contact')) return 'My Contacts';
    if (p.startsWith('/contacts')) return 'Contact List';
    if (p.startsWith('/calendar')) return 'Calendar';
    if (p.startsWith('/tasks')) return 'Task List';
    if (p.startsWith('/pipeline')) return 'Sales Pipeline';
    if (p.startsWith('/groups')) return 'Groups';
    if (p.startsWith('/reports')) return 'Reports';
    if (p.startsWith('/dashboard')) return 'Dashboard';
    if (p.startsWith('/search')) return 'Search';
    if (p.startsWith('/templates')) return 'Templates';
    if (p.startsWith('/settings')) return 'Settings';
    return 'SalesCrunchCRM';
  })();

  return (
    <div
      className="hidden md:flex items-center text-[11px] bg-gradient-to-b from-[#F5F5F5] to-[#E0E0E0] border-t border-[#999] text-[#333] shrink-0 select-none"
      data-testid="status-bar"
    >
      <span className="px-3 py-1 border-r border-[#BBB] flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
        Connected
      </span>
      <span className="px-3 py-1 border-r border-[#BBB]">
        Lookup: <strong className="text-[#0B5394]">{lookupLabel}</strong>
      </span>
      {counts && (
        <>
          <span className="px-3 py-1 border-r border-[#BBB]">
            {counts.total_contacts?.toLocaleString() || 0} Contact{counts.total_contacts === 1 ? '' : 's'}
          </span>
          <span className="px-3 py-1 border-r border-[#BBB]">
            {(counts.status_breakdown?.lead || 0)} Leads · {(counts.status_breakdown?.customer || 0)} Customers
          </span>
        </>
      )}
      <span className="flex-1" />
      <a
        href="https://app.termly.io/policy-viewer/policy.html?policyUUID=31b34c66-4334-40a3-b0ad-bfb742f1940f"
        target="_blank"
        rel="noopener noreferrer"
        className="px-2 py-1 border-l border-[#BBB] text-[#666] hover:text-[#0B5394]"
        data-testid="statusbar-privacy-link"
      >
        Privacy
      </a>
      <a
        href="https://app.termly.io/policy-viewer/policy.html?policyUUID=1d95cd3a-8063-4c28-b4e8-3f0253c39da8"
        target="_blank"
        rel="noopener noreferrer"
        className="px-2 py-1 border-l border-[#BBB] text-[#666] hover:text-[#0B5394]"
        data-testid="statusbar-terms-link"
      >
        Terms
      </a>
      <a
        href="https://app.termly.io/policy-viewer/policy.html?policyUUID=6fdd2b55-3320-465b-ae10-91351eeaebb3"
        target="_blank"
        rel="noopener noreferrer"
        className="px-2 py-1 border-l border-[#BBB] text-[#666] hover:text-[#0B5394]"
        data-testid="statusbar-cookies-link"
      >
        Cookies
      </a>
      <span className="px-3 py-1 border-l border-[#BBB] text-[#666]">
        {user?.name || 'User'} · {user?.role || 'user'}
      </span>
      <span className="px-3 py-1 border-l border-[#BBB] text-[#666]">
        v{appVersion || '—'}
      </span>
    </div>
  );
}
